import { attachUnitToResult, semanticEquals } from "@corca/sympy";

import {
  executeCode,
  getComputedExecutions,
} from "@/components/suggestions/providers/utils/getPhantomHints";
import { applySequenceEllipsis } from "@/components/suggestions/providers/utils/getSequenceHint";
import { getTableCellComputation } from "@/components/suggestions/providers/utils/getTableCellFormula";
import { getTableCellExecutions } from "@/components/suggestions/providers/utils/getTableCellHint";
import { ComputedReferenceNodeModel } from "@/models/nodes/ComputedReferenceNodeModel";
import { NumberNodeModel } from "@/models/nodes/NumberNodeModel";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import { isSupSubNode, isEqualsSign, isRelation, isSign } from "@/models/utils/semanticChecks";

import { ComputedResultNodeModel } from "../nodes/ComputedResultNodeModel";
import { InlineNodeModel } from "../nodes/InlineNodeModel";

import type { ComputedEvaluationResult } from "./types";
import type { HintExecution } from "@/components/suggestions/providers/utils/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";

/**
 * A standalone computed result: the wrapper's whole inline is a single `ComputedResultNodeModel`,
 * with no `LHS = ` in front of it. This is the table-cell shape — the formula lives in the row
 * header + column, not in the cell itself.
 */
export function isStandaloneComputedResult(wrapper: WrapperNodeModel): boolean {
  const nodes = wrapper.inlineNode?.nodes ?? [];
  return nodes.length === 1 && nodes[0].is(ComputedResultNodeModel);
}

/**
 * Executions to (re)evaluate a computed wrapper. A standalone table-cell result draws its formula
 * from the row header + column (via `getTableCellComputation`); every other computed wrapper
 * evaluates its own `LHS = ` expression against the wrappers in scope.
 */
function computedExecutions(
  doc: DocumentModel,
  wrapper: WrapperNodeModel,
  precedingWrapperIds: string[],
): HintExecution[] {
  const { tiptap } = doc.editor;
  if (tiptap && isStandaloneComputedResult(wrapper)) {
    const computation = getTableCellComputation(doc, tiptap, wrapper.id);
    if (computation) {
      return getTableCellExecutions(computation);
    }
  }
  return getComputedExecutions(doc, wrapper, precedingWrapperIds);
}

export async function evaluateComputedWrapper(
  doc: DocumentModel,
  wrapper: WrapperNodeModel,
  precedingWrapperIds: string[],
): Promise<ComputedEvaluationResult> {
  const executions = computedExecutions(doc, wrapper, precedingWrapperIds);

  let hadCode = false;
  // First result whose output is equivalent to the input (e.g. `evaluate` returning the expression
  // unchanged). Used only as a fallback if no command produces a genuinely different result.
  let equivalentFallback: ComputedEvaluationResult | null = null;

  for (const execution of executions) {
    if (!execution.pythonCode) continue;
    hadCode = true;

    // eslint-disable-next-line no-await-in-loop -- commands are tried in priority order
    const res = await executeCode(
      execution.pythonCode,
      execution.symbolNodes,
      execution.inputHasDecimal,
    );

    if (res && res.json.children.length > 0) {
      const json = execution.inputUnitResult
        ? attachUnitToResult(res.json, execution.inputUnitResult)
        : res.json;

      applySequenceEllipsis(json, execution);

      const result: ComputedEvaluationResult = {
        status: "ok",
        nodes: json.children,
        usedWrapperIds: execution.usedWrapperIds,
      };

      // Prefer a result that actually transforms the input, mirroring the phantom-hint check:
      // skip commands whose (normalized) output matches the input. Eager-eval functions (gcd/lcm)
      // and matrix results always count as a real result.
      const equals =
        !execution.hasEagerFunction &&
        !execution.hasMatrixResult &&
        execution.inputSemantic !== undefined &&
        semanticEquals(execution.inputSemantic, res.outputSemantic);

      if (!equals) return result;
      equivalentFallback ??= result;
    }
  }

  if (equivalentFallback) return equivalentFallback;
  return hadCode ? { status: "error" } : { status: "empty" };
}

/** Whether the cursor currently sits inside `node` (in it or any descendant). */
function isNodeFocused(doc: DocumentModel, node: NodeModel): boolean {
  const cursorNode = doc.cursor.node;
  if (!cursorNode) return false;
  return cursorNode === node || cursorNode.ancestors.includes(node);
}

export function isGroupFocused(doc: DocumentModel, wrapper: WrapperNodeModel): boolean {
  if (isNodeFocused(doc, wrapper)) return true;
  if (!wrapper.refId) return false;
  return doc
    .getNodesByType("computedReference")
    .some((ref) => ref.targetRefId === wrapper.refId && isNodeFocused(doc, ref));
}

export function collectReferenceNodes(
  node: NodeModel,
  result: ComputedReferenceNodeModel[] = [],
): ComputedReferenceNodeModel[] {
  if (node.is(ComputedReferenceNodeModel)) {
    result.push(node);
  }
  for (const child of node.nodes) {
    collectReferenceNodes(child, result);
  }
  return result;
}

export function findCyclicWrapperIds(deps: Map<string, string[]>): Set<string> {
  const cyclic = new Set<string>();
  for (const [id, directDeps] of deps) {
    const visited = new Set<string>();
    const queue = [...directDeps];
    while (queue.length > 0) {
      const current = queue.shift()!;
      if (current === id) {
        cyclic.add(id);
        break;
      }
      if (visited.has(current)) continue;
      visited.add(current);
      queue.push(...(deps.get(current) ?? []));
    }
  }
  return cyclic;
}

export function countEqualsSigns(nodes: NodeModel[]): number {
  return nodes.filter(isEqualsSign).length;
}

export function getEquationParts(nodes: NodeModel[]): [NodeModel[], NodeModel[]] | [] {
  const eqIndex = nodes.findIndex(isEqualsSign);
  if (eqIndex === -1) return [];
  const lhs = nodes.slice(0, eqIndex);
  const rhs = nodes.slice(eqIndex + 1);
  return [lhs, rhs];
}

export function getRhsNodes(wrapper: WrapperNodeModel): NodeModel[] {
  if (!wrapper.inlineNode) return [];
  const { nodes } = wrapper.inlineNode;
  const [, rhs] = getEquationParts(nodes);
  return rhs ?? [];
}

/**
 * The single result node of a computed equation: the `ComputedResultNodeModel`
 * sitting on the right-hand side (after the first top-level `=`), or undefined
 * when the equation has no result node yet.
 */
export function getComputedResultNode(
  node: WrapperNodeModel | InlineNodeModel,
): ComputedResultNodeModel | undefined {
  const inlineNode = node.is(InlineNodeModel) ? node : node.inlineNode;
  return inlineNode?.nodes.find((n) => n.is(ComputedResultNodeModel));
}

/**
 * RHS nodes other than the canonical result node. A non-empty list means the
 * user typed extra content on the result side — a signal that the computed flag
 * should be disabled.
 */
export function getRhsExtras(wrapper: WrapperNodeModel): NodeModel[] {
  const result = getComputedResultNode(wrapper);
  return getRhsNodes(wrapper).filter((n) => n !== result);
}

function siblingsInTerm(siblings: NodeModel[]): NodeModel[] {
  const relationIndex = siblings.findIndex(isRelation);
  return relationIndex === -1 ? siblings : siblings.slice(0, relationIndex);
}

const isNumber = (node?: NodeModel): boolean => !!node?.is(NumberNodeModel);

export function referenceNeedsBrackets(node: ComputedReferenceNodeModel): boolean {
  const value = node.inlineNode?.nodes ?? [];
  if (value.length === 0) return false;

  const touchesNumber =
    (isNumber(value[0]) && isNumber(node.prevSiblings[0])) ||
    (isNumber(value[value.length - 1]) && isNumber(node.nextSiblings[0]));
  if (touchesNumber) return true;

  if (value.length === 1) return false;

  const hasNodesBefore = siblingsInTerm(node.prevSiblings).length > 0;
  const hasNodesAfter = siblingsInTerm(node.nextSiblings).length > 0;
  const isSigned = value.length === 2 && isSign(value[0]);

  if (!hasNodesBefore && !hasNodesAfter) return false;
  if (!hasNodesBefore && isSigned && !isSupSubNode(node.nextSiblings[0])) return false;

  return true;
}

/** Stamps `bracketed` when it no longer matches `referenceNeedsBrackets`. */
export function syncReferenceBrackets(node: ComputedReferenceNodeModel): void {
  const bracketed = referenceNeedsBrackets(node);
  if (!!node.props.bracketed !== bracketed) {
    node.updateProps({ bracketed });
  }
}

export function createDependencyMap(doc: DocumentModel): Map<string, string[]> {
  const computedByRefId = new Map<string, string>();
  for (const w of doc.textNode.wrapperNodes) {
    if (w.computed && w.refId) {
      computedByRefId.set(w.refId, w.id);
    }
  }

  const deps = new Map<string, string[]>();
  for (const w of doc.textNode.wrapperNodes) {
    const refTargets = collectReferenceNodes(w)
      .map((ref) => computedByRefId.get(ref.targetRefId))
      .filter((id): id is string => !!id && id !== w.id);
    const usedWrapperIds = getComputedResultNode(w)?.props.usedWrapperIds ?? [];
    deps.set(w.id, [...new Set([...refTargets, ...usedWrapperIds])]);
  }

  return deps;
}

/**
 * Returns the set of wrapper IDs that would be transitively depended on by
 * `wrapperId` if added to the graph. Uses BFS through the dependency edges.
 */
export function getTransitiveDeps(wrapperId: string, deps: Map<string, string[]>): Set<string> {
  const visited = new Set<string>();
  const queue = [...(deps.get(wrapperId) ?? [])];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current === wrapperId) continue;
    if (visited.has(current)) continue;
    visited.add(current);
    queue.push(...(deps.get(current) ?? []));
  }
  return visited;
}

export function canReferenceAtCursor(doc: DocumentModel, node: ComputedResultNodeModel): boolean {
  // if (!doc.cursor.isActive) return false;

  const wrapperNode = node.ancestors.find((n) => n.is(WrapperNodeModel));
  const cursorWrapperNode = doc.cursor.parentWrapperNode;

  if (cursorWrapperNode && wrapperNode) {
    if (cursorWrapperNode.id === wrapperNode.id) return false;
    const deps = createDependencyMap(doc);
    const transitiveDeps = getTransitiveDeps(wrapperNode.id, deps);
    return !transitiveDeps.has(cursorWrapperNode.id);
  }

  return true;
}
